import {inject, Injectable} from '@angular/core';
import {defer, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {AppConfigService} from '../../../services/config.service';
import {DEX_RATE_REF_SYMBOL} from '../dex-streaming/const';
import {DexMdSubjectService} from '../dex-streaming/subjects/dex-md-subject-service';
import {DexSecuritiesSubjectService} from '../dex-streaming/subjects/dex-securities-subject-service';
import {Deliverable, DeliverableId} from '../dex-streaming/types/deliverable';
import {RefRate} from '../dex-streaming/types/ref-rate';
import {Tranche, TrancheId} from '../dex-streaming/types/tranche';
import {
    adjustDp,
    DecimalRounding,
    numToV,
    parseDexBigIntStr,
    ValueWithDel,
    valueWithDelToDec,
    vToDec
} from '../dex-streaming/utils';

@Injectable()
export class DexTrancheUtilityService {

    public readonly dexSecuritiesSubjectService = inject(DexSecuritiesSubjectService);
    public readonly dexMdSubjectService = inject(DexMdSubjectService);
    public readonly appConfigService = inject(AppConfigService);

    private trancheIdBySymbolCache: Map<string, TrancheId> | undefined = undefined;
    private defaultTrancheIdCache: TrancheId | undefined = undefined;
    private trancheBuyDelIdsCache: Map<TrancheId, Set<DeliverableId> | undefined> | undefined = undefined;
    private nativeCurrencyDelCache: Deliverable | undefined = undefined;

    public getNativeDel(): Deliverable {
        this.nativeCurrencyDelCache ??= this.dexSecuritiesSubjectService.getDeliverableBySymbol(
            this.appConfigService.appConfig.dex.chain.nativeCurrency.symbol
        );

        return this.nativeCurrencyDelCache;
    }

    public getTrancheBySymbol(symbol: string): Tranche | undefined {
        this.initTrancheIdBySymbolCache();
        const trancheId = this.trancheIdBySymbolCache.get(symbol);

        return trancheId && this.dexMdSubjectService.trancheMapById.get(trancheId);
    }

    public getDefaultTranche(): Tranche | undefined {
        let tranche: Tranche | undefined;

        if (!this.defaultTrancheIdCache) {
            for (const itTranche of this.dexMdSubjectService.trancheMapById.values()) {
                if (!tranche || itTranche.poolTrancheId < tranche.poolTrancheId) {
                    tranche = itTranche;
                }
            }

            this.defaultTrancheIdCache = tranche?.poolTrancheId;
        } else {
            tranche = this.dexMdSubjectService.trancheMapById.get(this.defaultTrancheIdCache);
        }

        return tranche;
    }

    public getDefaultTrancheSymbol(): string | undefined {
        const tranche = this.getDefaultTranche();

        return tranche
            && this.dexSecuritiesSubjectService.getDeliverableById(tranche.unitDeliverableId)?.symbol;
    }

    public isSymbolUpdated<S extends {symbol: string}>(
        modelUpdate: Partial<S>,
        prevModel: Partial<S>
    ): boolean {
        return !!modelUpdate?.symbol
            && modelUpdate.symbol !== prevModel?.symbol;
    }

    public isAssetSymbolUpdated<S extends {assetSymbol: string}>(
        modelUpdate: Partial<S>,
        prevModel: Partial<S>
    ): boolean {
        return !!modelUpdate?.assetSymbol
            && modelUpdate.assetSymbol !== prevModel?.assetSymbol;
    }

    public getTrancheSymbolFix<S extends {symbol?: string}>(
        modelUpdate: Partial<S>,
        prevModel: Partial<S>
    ): Observable<{symbol?: string} | undefined> {
        const getDefaultSymbol = () => {
            const symbol = this.getDefaultTrancheSymbol();

            return symbol ? {symbol} : undefined;
        };

        return defer(() =>
            this.isSymbolUpdated(modelUpdate, prevModel)
                ? this.getTrancheBySymbol(modelUpdate.symbol)
                    ? of(undefined)
                    : of(getDefaultSymbol())
                : !prevModel?.symbol // Set to default if not defined
                    ? of(getDefaultSymbol())
                    : of(undefined)
        );
    }

    public isDelAllowedForBuy(trId: TrancheId, dId: DeliverableId): boolean {
        this.initTrancheBuyDelIdsCache();

        return this.trancheBuyDelIdsCache.get(trId)?.has(dId) || false;
    }

    public getDelAllowedForBuySet(trId: TrancheId): ReadonlySet<DeliverableId> | undefined {
        this.initTrancheBuyDelIdsCache();

        return this.trancheBuyDelIdsCache.get(trId);
    }

    public getDefaultDelAllowedForBuy(trId: TrancheId): Deliverable | undefined {
        this.initTrancheBuyDelIdsCache();
        const set = this.trancheBuyDelIdsCache.get(trId);
        let dId: DeliverableId | undefined;

        if (set) {
            for (const itDelId of set.values()) {
                if (!dId || itDelId < dId) {
                    dId = itDelId;
                }
            }
        }

        return dId && this.dexSecuritiesSubjectService.getDeliverableById(dId);
    }

    public getMinBuyQty(trId: TrancheId, d: Deliverable, rate?: RefRate): ValueWithDel {
        const buyRules = this.dexMdSubjectService.trancheRulesMapById.get(trId);
        const minValue = parseDexBigIntStr(buyRules?.depositRules?.minValue);
        const minByDecimals = 1n;

        if (!minValue) {
            return {d, v: minByDecimals};
        }

        const refDel = this.dexSecuritiesSubjectService.getDeliverableBySymbol(DEX_RATE_REF_SYMBOL);

        if (!refDel) {
            return {d, v: minByDecimals};
        }

        rate ??= this.dexMdSubjectService.getRefRateByPair(d.deliverableId, refDel.deliverableId);

        if (!rate?.price) {
            return {d, v: minByDecimals};
        }

        const minByRateDec = vToDec(minValue, refDel.decimalPlaces).div(vToDec(rate.price, refDel.decimalPlaces));
        const minByRate = numToV(minByRateDec, d.decimalPlaces, DecimalRounding.UP);

        return {d, v: minByRate};
    }

    public getMaxBuyQty(trId: TrancheId, d: Deliverable, rate?: RefRate): ValueWithDel | undefined {
        const buyRules = this.dexMdSubjectService.trancheRulesMapById.get(trId);
        const maxValue = parseDexBigIntStr(buyRules?.depositRules?.maxValue);

        if (!maxValue) {
            return undefined;
        }

        const refDel = this.dexSecuritiesSubjectService.getDeliverableBySymbol(DEX_RATE_REF_SYMBOL);

        if (!refDel) {
            return undefined;
        }

        rate ??= this.dexMdSubjectService.getRefRateByPair(d.deliverableId, refDel.deliverableId);

        if (!rate?.price) {
            return undefined;
        }

        const maxByRateDec = vToDec(maxValue, refDel.decimalPlaces).div(vToDec(rate.price, refDel.decimalPlaces));
        const maxByRate = numToV(maxByRateDec, d.decimalPlaces, DecimalRounding.DOWN);

        return {d, v: maxByRate};
    }

    public getBuyMinMaxNumStream(trId: TrancheId, d: Deliverable): Observable<
        {min: number, max: number, refMin: bigint, refMax: bigint}
    > {
        const refDel = this.dexSecuritiesSubjectService.getDeliverableBySymbol(DEX_RATE_REF_SYMBOL);

        return this.dexMdSubjectService.getRefRateStream(d.deliverableId, refDel.deliverableId, true).pipe(
            map(rate => {
                const minV = this.getMinBuyQty(trId, d, rate);
                const maxV = this.getMaxBuyQty(trId, d, rate);
                const minNum = valueWithDelToDec(minV).toNumber();
                const maxNum = maxV && valueWithDelToDec(maxV).toNumber();

                return {min: minNum, max: maxNum, refMin: minV?.v, refMax: maxV?.v};
            })
        );
    }

    public nativeTokenToVd(v: bigint | undefined): ValueWithDel {
        const nativeToken = this.appConfigService.appConfig.dex.chain.nativeCurrency;
        const d = this.getNativeDel();

        return {
            v: v && adjustDp(v, nativeToken.decimals, d.decimalPlaces),
            d
        };
    }

    private initTrancheIdBySymbolCache(): void {
        if (!this.trancheIdBySymbolCache) {
            this.trancheIdBySymbolCache = new Map();

            for (const tr of this.dexMdSubjectService.trancheMapById.values()) {
                const d = this.dexSecuritiesSubjectService.getDeliverableById(tr.unitDeliverableId);

                if (d) {
                    this.trancheIdBySymbolCache.set(d.symbol, tr.poolTrancheId);
                }
            }
        }
    }

    private initTrancheBuyDelIdsCache(): void {
        if (!this.trancheBuyDelIdsCache) {
            this.trancheBuyDelIdsCache = new Map();

            for (const tr of this.dexMdSubjectService.trancheMapById.values()) {
                const trId = tr.poolTrancheId;
                const rByDelId = this.dexMdSubjectService.trancheDeliverableRulesMapById.get(trId);

                if (rByDelId) {
                    const buySet = new Set<DeliverableId>();

                    for (const r of rByDelId.values()) {
                        if (r.participationRules.depositsAllowed) {
                            buySet.add(r.deliverableId);
                        }
                    }

                    if (buySet.size) {
                        this.trancheBuyDelIdsCache.set(trId, buySet);
                    }
                }
            }
        }
    }

}
